import { useLang } from "@/lib/i18n";
import { whatsappUrl } from "@/lib/translations";

export const Hero = () => {
  const { lang, t } = useLang();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 lg:pb-32 overflow-hidden"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Top meta row */}
        <div className="flex items-center justify-between mb-12 md:mb-20">
          <div className="overline">{t.hero.overline}</div>
          <div className="hidden md:flex items-center gap-2 overline">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span>{t.hero.meta}</span>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-y-12 lg:gap-x-12 items-start">
          {/* Left: Headline */}
          <div className="col-span-12 lg:col-span-8">
            <h1
              data-testid="hero-headline"
              className="editorial-h text-[58px] sm:text-[78px] md:text-[104px] lg:text-[128px] xl:text-[148px]"
            >
              {t.hero.titleA}{" "}
              <span className="italic text-neutral-500">{t.hero.titleB}</span>
              <br />
              <span className="block mt-1 md:mt-2">{t.hero.titleC}</span>
              <span className="block italic text-neutral-500">
                {t.hero.titleD}
              </span>
            </h1>
          </div>

          {/* Right: Image + paragraph */}
          <div className="col-span-12 lg:col-span-4 lg:pt-6 flex flex-col gap-8">
            <div className="hover-zoom aspect-[4/5] w-full max-w-md lg:max-w-none lg:ml-auto">
              <img
                src="https://images.unsplash.com/photo-1533090161767-e6ffed986c88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY2xlYW4lMjBvZmZpY2UlMjBkZXNrfGVufDB8fHx8MTc4MjIwOTc1M3ww&ixlib=rb-4.1.0&q=85"
                alt="Estudio Girona Webs"
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>
            <p
              data-testid="hero-subtitle"
              className="text-[17px] md:text-[18px] leading-[1.55] text-neutral-700 max-w-md"
            >
              {t.hero.subtitle}
            </p>
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-14 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
          <a
            data-testid="hero-cta-whatsapp"
            href={whatsappUrl(lang)}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 px-7 py-4 bg-black text-white hover:bg-neutral-800 transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
            <span className="text-[13px] tracking-[0.15em] uppercase">
              {t.hero.ctaPrimary}
            </span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="transition-transform group-hover:translate-x-1"
            >
              <path d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </a>
          <a
            data-testid="hero-cta-secondary"
            href="#work"
            className="group inline-flex items-center gap-2 px-1 py-4 border-b border-black text-[13px] tracking-[0.15em] uppercase hover:gap-3 transition-all"
          >
            <span>{t.hero.ctaSecondary}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M7 17L17 7M7 7h10v10" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};
